import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageShell from "./_PageShell";

const INITIATIVES = [
  {
    n: "01",
    title: "Mathematics & STEM Tutoring",
    body:
      "Personalized mathematics tutoring and STEM coaching for middle school, high school, and university preparation. Helping students build confidence, master difficult concepts, and develop strong mathematical thinking rather than memorizing procedures.",
    cta: "Explore Tutoring",
    href: "/consulting/math-stem-tutoring",
  },
  {
    n: "02",
    title: "Youth Mentorship & Guidance",
    body:
      "One-to-one mentorship for young men navigating faith, motivation, emotional growth, leadership, and personal development.",
    cta: "Learn More",
    href: "/consulting/youth-mentorship-guidance",
  },
  {
    n: "03",
    title: "Educator Development",
    body:
      "Professional development workshops helping educators move beyond information delivery toward transformative teaching and Murabbi-centered education.",
    cta: "Learn More",
    href: "/contact",
  },
  {
    n: "04",
    title: "Webinars & Seminars",
    body:
      "Conversations and training sessions on mentorship, emotional intelligence, education, tarbiyah, leadership, and youth development.",
    cta: "Learn More",
    href: "/contact",
  },
  {
    n: "05",
    title: "Career Counselling",
    body:
      "Dedicated career counselling for secondary school students preparing for life after graduation. Helping young people identify strengths, explore university pathways, trade schools, military service, entrepreneurship, and direct workforce opportunities while developing the professional skills needed to thrive in a rapidly changing world.",
    cta: "Explore Career Counselling",
    href: "/consulting/career-counselling",
  },
];

const Consulting = () => (
  <PageShell
    testId="consulting-page"
    eyebrow="04 — Formation & Initiatives"
    title="Formation Beyond Information."
    intro="Programs, mentorship, and educational initiatives rooted in the belief that meaningful growth begins with the cultivation of the human being."
  >
    {/* Manifesto callout */}
    <figure className="relative max-w-3xl mb-10 lg:mb-14">
      <span
        aria-hidden="true"
        className="absolute -left-3 -top-6 font-serif text-azure-soft/45 text-[90px] leading-none select-none pointer-events-none"
      >
        “
      </span>
      <blockquote className="relative pl-7 border-l border-azure-soft/30">
        <p className="font-serif text-white/90 text-[18px] lg:text-[20px] leading-[1.4] tracking-[-0.005em]">
          We do not need more programs.
        </p>
        <p className="font-serif italic text-azure-soft text-[18px] lg:text-[20px] leading-[1.4] tracking-[-0.005em]">
          We need formation.
        </p>
      </blockquote>
      <figcaption className="mt-5 pl-7 text-[10.5px] tracking-[0.34em] uppercase text-white/45">
        — The Murabbi Method
      </figcaption>
    </figure>

    {/* Initiative cards */}
    <div className="grid md:grid-cols-2 gap-6 lg:gap-7">
      {INITIATIVES.map((item, i) => (
        <Link
          key={item.n}
          to={item.href}
          data-testid={`consulting-initiative-${i}`}
          className={`group relative flex flex-col border border-white/10 hover:border-azure/40 bg-ink-soft/40 transition-colors duration-500 ${
            i === INITIATIVES.length - 1 && INITIATIVES.length % 2 === 1
              ? "md:col-span-2 md:justify-self-center md:w-[calc(50%-0.875rem)]"
              : ""
          }`}
        >
          {/* Hover wash */}
          <span
            aria-hidden="true"
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 80% 100% at 0% 0%, rgba(74,143,229,0.10), transparent 70%)",
            }}
          />

          <div className="relative p-6 lg:p-8 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <span className="text-[10.5px] tracking-[0.34em] uppercase text-azure-soft">
                Initiative {item.n}
              </span>
              <span className="font-serif italic text-azure-soft/70 text-[28px] leading-none tracking-[-0.02em]">
                {item.n}
              </span>
            </div>

            <div className="mt-7 h-px w-12 bg-azure-soft/50" />

            <h3 className="mt-7 font-serif text-white text-[20px] lg:text-[24px] leading-[1.18] tracking-[-0.015em] group-hover:text-azure-soft transition-colors duration-500">
              {item.title}
            </h3>

            <p className="mt-5 text-white/65 text-[15px] leading-[1.9] max-w-[60ch]">
              {item.body}
            </p>

            <div className="mt-auto pt-8 inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-white/55 group-hover:text-azure-soft transition-colors duration-500">
              {item.cta}
              <ArrowUpRight
                className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1.5 group-hover:-translate-y-1.5"
                strokeWidth={1.6}
              />
            </div>
          </div>
        </Link>
      ))}
    </div>
  </PageShell>
);

export default Consulting;
